import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import StbButton from "../../atoms/button/StbButton";
import "./CreateProjectBriljantIntegration.css";
import { useQueryClient } from "@tanstack/react-query";
import { ICreateProjectIntegrationProps } from "./CreateProjectBriljantIntegration.types";
import DropDownSelectDepartment from "../../molecules/dropdown-select-department/DropDownSelectDepartment";
import {
  useGetApiErpImportGetAllDepartments,
  useGetApiErpImportGetAllProjectsByDepartment,
} from "../../../api/generated/endpoints/erp-import/erp-import";
import DropDownSelectProject from "../../molecules/dropdown-select-project/DropDownSelectProject";
import LoadingSection from "../../atoms/loadingsection/LoadingSection";
import { useNavigate } from "react-router-dom";

const ERP_LIST_STALE_TIME_MS = 5 * 60 * 1000;

const CreateProjectBriljantIntegration: React.FC<
  ICreateProjectIntegrationProps
> = (props) => {
  const {
    department: initialDepartment,
    setDepartment: onDepartmentChange,
    setErpReference: onErpReferenceChange,
    setSelectedProjectNumber: onProjectNumberChange,
  } = props;
  const queryClient = useQueryClient();
  const [selectedDepartment, setSelectedDepartment] = useState<number | undefined>(initialDepartment);
  const [selectedProjectNumber, setSelectedProjectNumber] = useState<string>();
  const [selectedErpReference, setSelectedErpReference] = useState<string | undefined>();
  const [department, setDepartment] = useState<number>();
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedProjectNumber(undefined);
    setSelectedErpReference(undefined);
  }, [selectedDepartment])

  const { data: projects, isLoading: isProjectsLoading } = useGetApiErpImportGetAllProjectsByDepartment(
    selectedDepartment ? { department: selectedDepartment } : undefined,
    {
      query: {
        enabled: !!selectedDepartment,
        staleTime: ERP_LIST_STALE_TIME_MS,
      },
    }
  );

  const { data: departments, isLoading: isDepartmentsLoading } = useGetApiErpImportGetAllDepartments({
    query: {
      staleTime: ERP_LIST_STALE_TIME_MS,
    },
  });

  // Ensure selectedDepartment is set after departments data loads
  useEffect(() => {
    if (departments && initialDepartment && !selectedDepartment) {
      // Verify that the department from props exists in the loaded departments
      const departmentExists = departments.some(dept => dept.avd === initialDepartment);
      if (departmentExists) {
        setSelectedDepartment(initialDepartment);
      }
    }
  }, [departments, initialDepartment, selectedDepartment]);

  // Update URL when department changes
  useEffect(() => {
    if (selectedDepartment !== undefined) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set('department', selectedDepartment.toString());
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
      
      // Also update the parent component's state
      onDepartmentChange(selectedDepartment);
    }
  }, [selectedDepartment, onDepartmentChange]);

  if (isDepartmentsLoading) { return <LoadingSection text="Laddar.." /> }

  return (
    <Container>
      <Row className="w-full d-flex justify-content-center">
        <Col xs={11} sm={12} md={10} l={9} xl={8} xxl={6}>
          <DropDownSelectDepartment 
            setSelectedDepartment={setSelectedDepartment} 
            departments={departments} 
            selectedDepartment={selectedDepartment}
          />
        </Col>
      </Row>
      <Row className="w-full d-flex justify-content-center mt-3">
        <Col xs={11} sm={12} md={10} l={9} xl={8} xxl={6}>
          <DropDownSelectProject projects={projects} setSelectedProjectNumber={setSelectedProjectNumber} setErpReference={setSelectedErpReference} setDepartment={setDepartment} />
        </Col>
      </Row>
      <Row className="d-flex justify-content-center mt-3">
        <Col xs="auto">
          <StbButton
            variant="primary"
            disabled={!selectedProjectNumber}
            type="submit"
            onClick={(e) => {
            e.preventDefault();
            queryClient.invalidateQueries({ queryKey: ["project"] });
            onProjectNumberChange(selectedProjectNumber);
            onErpReferenceChange(selectedErpReference);
            // Find selected project for customerId and department
            const selectedProject = projects?.find(
              (p) => String(p.projectId ?? "") === selectedProjectNumber
            );
            if (selectedProject) {
              // Include projectNumber, erpReference and department in the URL
              const urlParams = new URLSearchParams();
              urlParams.set('projectNumber', selectedProjectNumber ?? "");
              urlParams.set('department', (department || 0).toString());
              urlParams.set('erpReference', selectedErpReference || '');
              navigate(`/createproject?${urlParams.toString()}`);
            }
            }}
          >
            Hämta
          </StbButton>
        </Col>
      </Row>
      {isProjectsLoading && <LoadingSection text="Laddar.." />}
    </Container>
  );
};

export default CreateProjectBriljantIntegration;
